import { AnimatePresence, motion } from 'framer-motion';
import type { PropsWithChildren } from 'react';

import { QBox, QStack } from '@captivateiq/quotient';

import { AIShellContextProvider, useAIShellContext } from './AIShellContext';
import { AIShell } from './components/AIShell';
import type { APIService, IContextDetection, UserService } from './interfaces/services';

const AIShellAnimated = () => {
  const { isOpen } = useAIShellContext();

  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          animate={{ width: 444, opacity: 1 }}
          data-testid="ai-shell-panel"
          exit={{ width: 0, opacity: 0 }}
          initial={{ width: 0, opacity: 0 }}
          key="ai-shell-overlay"
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            height: '100vh',
            backgroundColor: '#fff',
            boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.06)',
            zIndex: 1010, // Ensure shell appears above other content but below modals
            overflow: 'hidden',
          }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
        >
          <AIShell />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const MainContent = ({ children }: PropsWithChildren<{}>) => {
  const { isOpen } = useAIShellContext();
  return (
    <QBox
      sx={{
        width: isOpen ? 'calc(100% - 444px)' : '100%',
        transition: 'width 0.2s ease-out',
        '@media (max-width: 768px)': {
          width: isOpen ? '0%' : '100%', // Hide main content on mobile when sidebar is open
        },
      }}
    >
      {children}
    </QBox>
  );
};

type AIShellWrapperProps = PropsWithChildren<{
  userService: UserService;
  apiService: APIService;
  /**
   * Context detection adapter for URL-based context extraction.
   * Required for context-specific agents to be filtered correctly.
   */
  contextDetection?: IContextDetection;
}>;

export const AIShellWrapper = ({ children, userService, apiService, contextDetection }: AIShellWrapperProps) => {
  return (
    <QStack direction="row" sx={{ justifyContent: 'stretch' }}>
      <AIShellContextProvider apiService={apiService} contextDetection={contextDetection} userService={userService}>
        <MainContent>{children}</MainContent>
        <AIShellAnimated />
      </AIShellContextProvider>
    </QStack>
  );
};
